import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";

import { ItemEditPage } from "@/components/catalog/item-edit-page";
import { catalogItemDetailQueryOptions } from "@/hooks/catalog-items";
import { usePermissions } from "@/hooks/permissions";
import { itemDetailPath } from "@/lib/route-paths";
import { requireCatalogAccess } from "@/lib/route-guards";
import { SPECTOOL_DIVISION_EDITOR } from "@/lib/roles";
import { useCurrentUser } from "@/hooks/current-user";

// One item's edit page — the donor's `/products/:assetId/edit`.
//
// **A sibling, not a child.** The trailing underscore on `$itemId_` opts this
// route out of nesting under `$itemId.tsx`, which stays a leaf rendering the
// details page rather than becoming a layout with an outlet it has nowhere to
// put.
//
// The route owns what the page component deliberately does not: the permission
// gate and the redirect it fires, and the document title. The reads, the save
// and the form are the page's.

export const Route = createFileRoute("/_authed/_chrome/items/$itemId_/edit")({
  beforeLoad: ({ context }) => {
    requireCatalogAccess(context.currentUser);
  },
  /**
   * **A different item is a different form.** TanStack keys the route component
   * on `remountDeps` and nothing else — with none declared the key is
   * `undefined` (`react-router/Match.js`, `MatchInner`), so a navigation that
   * only changes `$itemId` reuses the mounted component. The page's draft is
   * `useState`, so it would survive: item A's typed values under item B's
   * heading, and a Save that PATCHes **B** with them, because the baseline
   * recomputes for B while the draft does not.
   *
   * The donor has the same hole — `formFieldsAreInitialized` is a ref, so its
   * `reset` never re-runs for the new `assetId` (`useEditProduct.ts:134-141`)
   * — and this is the one place the port declines to reproduce it: nothing a
   * modsy user can see differs, the only observable is a cross-item write, and
   * a stale draft saved onto the wrong row is not a behaviour worth porting.
   */
  remountDeps: ({ params }) => params.itemId,
  /**
   * Warm the details read — for the title below, and to answer a stale link.
   *
   * The loader is the **only** place a `notFound()` works. Thrown from the
   * component instead, TanStack's catch boundary responds by re-rendering the
   * route, which throws again, until React gives up and the app crash boundary
   * shows "the app hit an unexpected error" — verified on the running app, and
   * the sibling details route has the same defect (noted on #253 for the owner;
   * its fix is this same loader, and is not this ticket's to make).
   *
   * **Both failure modes land on the 404**, because both do in the donor:
   *
   *   - **The read succeeded and the item is not there** (`nullOn:
   *     CATALOG_ITEM_NOT_FOUND_TAG` in the transport turns the tagged 404 into a
   *     plain `null`) — a stale or hand-typed id.
   *   - **The read failed** — a down service, a bad id shape. The donor watches
   *     for exactly this and leaves the screen: `if (productQuery.isError)
   *     navigate('*')` (`useEditProduct.ts:123-127`), and `*` is `<ErrorPage />`
   *     (`App.tsx:117`), which is `<NotFoundError />` and nothing else
   *     (`error-page/error-page.tsx`). Its edit page has no read-error snackbar
   *     at all — the `Snackbar` it renders is the save result
   *     (`product-edit-page.tsx:87-102`).
   */
  loader: async ({ context, params }) => {
    const data = await context.queryClient
      .ensureQueryData(catalogItemDetailQueryOptions(Number(params.itemId)))
      // A read failure and an absent item are the same answer here, so the
      // failure just becomes the absence and falls into the raise below.
      .catch(() => null);
    if ((data ?? null) === null) throw notFound();
    return data;
  },
  // `Item Edit | {name}` — the donor's `useSetPageTitle`, minus its habit of
  // rendering `Item Edit | undefined` before the item lands.
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.item?.name ? `Item Edit | ${loaderData.item.name}` : "Item Edit" }],
  }),
  component: ItemEditRoute,
});

/**
 * The refusal, in two sentences (`product-edit-page.tsx:44-65`'s shape).
 *
 * **The first is the donor's, verbatim. The second is not** (owner ruling,
 * #553 D3). The donor's addendum — "You are a Division Editor and can only edit
 * items that belong to a single division." — describes its single-division
 * exception, which #651 D2 removed as a named parity break: a Division Editor
 * may edit an item only when every division using it is in their editable set.
 * So the addendum says that rule, in the owner's words.
 *
 * The addendum is appended only for a division editor. In the donor neither is
 * ever read — `selectItemsError` has no importers, so the user is redirected in
 * silence — but #253 asks for "an explanatory error and a redirect", so the
 * message is actually shown. A knowing divergence, in the direction the ticket
 * specifies, and named in the parity enumeration on that ticket.
 */
const NO_EDIT_PERMISSION = "You do not have permission to edit this item.";
const DIVISION_EDITOR_ADDENDUM =
  "You must have edit permissions in all divisions using this item in order to edit.";

function ItemEditRoute() {
  const { itemId } = Route.useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { data } = useCurrentUser();
  const detail = Route.useLoaderData();
  const id = Number(itemId);

  // The donor's `canEdit(itemData)` (`product-edit-page.tsx:56-62`), both
  // halves: the role, and the per-item division narrowing. The narrowing is
  // Python's `assert_may_edit_item` — the rule the save itself runs — answered
  // as `mayEdit` on the detail read the loader has already awaited (#666 D1),
  // so a Division Editor on an item another division uses is turned back
  // before the form renders, never refused only at Save.
  const canEdit = permissions.canWriteCatalogItems && detail?.mayEdit === true;
  const isDivisionEditor = data?.session.roles?.includes(SPECTOOL_DIVISION_EDITOR) ?? false;

  useEffect(() => {
    if (canEdit) return;
    // Deny-by-default while the identity is still resolving is `usePermissions`'
    // own answer, so a user with rights sees at worst one frame of nothing on
    // the way to their form — never the reverse.
    toast.error(
      isDivisionEditor ? `${NO_EDIT_PERMISSION} ${DIVISION_EDITOR_ADDENDUM}` : NO_EDIT_PERMISSION,
      { duration: 10_000, position: "bottom-center" },
    );
    void navigate({ to: itemDetailPath(id), replace: true });
  }, [canEdit, isDivisionEditor, id, navigate]);

  if (!canEdit) return null;

  return <ItemEditPage itemId={id} canSetInternalUseOnly={permissions.canSetItemInternalUseOnly} />;
}
